import {
  getPlotBoundsRelativeToOverlay,
  getSeriesInfoFromUPlot,
  nextToolId,
  VisibleSensorInfo,
} from 'features/timeSeries/components/rulerTool/useRulerTool'
import { getSensorColor } from 'features/timeSeries/config/timeSeriesColors'
import React, { useCallback, useRef } from 'react'
import uPlot from 'uplot'

export interface PointAnnotation {
  id: string
  anchorTimestamp: number
  anchorYRatio: number
  sensorId: string | null
}

interface UsePointAnnotationToolProps {
  uPlotRef: React.RefObject<uPlot | null>
  allSensorsToRender: Array<{ id: string; name: string }>
  customSensorColors?: Record<string, string>
  isActive: boolean
  /**
   * Annotation state is owned by `ScatterplotProvider`, not by this hook, so
   * that saving a chart can see it and loading one can restore it. The hook
   * stays the only thing that *mutates* it.
   */
  annotations: PointAnnotation[]
  setAnnotations: React.Dispatch<React.SetStateAction<PointAnnotation[]>>
}

export function findNearestValidIndex(
  xValues: ArrayLike<number>,
  yValues: ArrayLike<number | null | undefined>,
  targetXValue: number
): number {
  let nearestIndex = -1
  let nearestDistance = Number.POSITIVE_INFINITY

  for (let index = 0; index < xValues.length; index += 1) {
    const value = yValues[index]
    if (typeof value !== 'number' || Number.isNaN(value)) {
      continue
    }

    const distance = Math.abs(Number(xValues[index]) - targetXValue)
    if (distance < nearestDistance) {
      nearestDistance = distance
      nearestIndex = index
    }
  }

  return nearestIndex
}

export function findNearestIndexByTime(
  xValues: ArrayLike<number>,
  targetXValue: number
): number {
  let nearestIndex = 0
  let nearestDistance = Number.POSITIVE_INFINITY

  for (let index = 0; index < xValues.length; index += 1) {
    const distance = Math.abs(Number(xValues[index]) - targetXValue)
    if (distance < nearestDistance) {
      nearestDistance = distance
      nearestIndex = index
    }
  }

  return nearestIndex
}

export function usePointAnnotationTool({
  uPlotRef,
  allSensorsToRender,
  customSensorColors,
  isActive,
  annotations,
  setAnnotations,
}: UsePointAnnotationToolProps) {
  const lastVisibleSensorsRef = useRef<VisibleSensorInfo[]>([])

  const getSensorColorById = useCallback(
    (sensorId: string): string => {
      if (customSensorColors?.[sensorId]) return customSensorColors[sensorId]
      const index = allSensorsToRender.findIndex(
        (sensor) => sensor.id === sensorId
      )
      return getSensorColor(index >= 0 ? index : 0)
    },
    [allSensorsToRender, customSensorColors]
  )

  const getVisibleSensors = useCallback((): VisibleSensorInfo[] => {
    const u = uPlotRef.current
    if (!u) return lastVisibleSensorsRef.current

    const visibleSensors = getSeriesInfoFromUPlot(u)
      .filter((series) => series.show && !series.isRegression)
      .map((series) => ({
        sensorId: series.sensorId,
        name: series.name,
        color: getSensorColorById(series.sensorId),
      }))

    lastVisibleSensorsRef.current = visibleSensors
    return visibleSensors
  }, [getSensorColorById, uPlotRef])

  const getClampedAnchor = useCallback(
    (
      e: React.MouseEvent<HTMLDivElement>
    ): {
      anchorTimestamp: number
      anchorYRatio: number
      sensorId: string | null
    } | null => {
      const u = uPlotRef.current
      if (!u) return null
      const overlayEl = e.currentTarget
      const overlayRect = overlayEl.getBoundingClientRect()
      const { overLeft, overRight, overTop } = getPlotBoundsRelativeToOverlay(
        overlayEl,
        u
      )
      const overBottom = u.over.getBoundingClientRect().bottom - overlayRect.top
      const mouseXOnOverlay = e.clientX - overlayRect.left
      const mouseYOnOverlay = e.clientY - overlayRect.top
      const relXInPlot = Math.max(
        overLeft,
        Math.min(overRight, mouseXOnOverlay)
      )
      const relYInPlot = Math.max(
        overTop,
        Math.min(overBottom, mouseYOnOverlay)
      )
      const xValue = u.posToVal(relXInPlot - overLeft, 'x')
      const xValues = u.data[0] as ArrayLike<number> | undefined
      if (!xValues || xValues.length === 0) return null
      const nearestIndex = Math.max(
        0,
        Math.min(xValues.length - 1, findNearestIndexByTime(xValues, xValue))
      )
      const timestamp = Number(xValues[nearestIndex])
      const plotHeight = Math.max(1, overBottom - overTop)
      const anchorYRatio = (relYInPlot - overTop) / plotHeight
      const nearestSensorId = getNearestSensorIdAtPosition(
        u,
        timestamp,
        relXInPlot - overLeft,
        relYInPlot - overTop
      )

      return Number.isFinite(timestamp)
        ? {
            anchorTimestamp: timestamp,
            anchorYRatio,
            sensorId: nearestSensorId,
          }
        : null
    },
    [uPlotRef]
  )

  const handleOverlayMouseMove = useCallback(() => {}, [])

  const handleOverlayMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isActive || e.button !== 0) return

      const anchor = getClampedAnchor(e)
      if (anchor === null) return

      e.preventDefault()
      e.stopPropagation()

      // Mint the id from the current list so restored annotations are taken
      // into account and a reloaded chart cannot collide on `point-annotation-1`.
      setAnnotations((currentAnnotations) => [
        ...currentAnnotations,
        {
          id: nextToolId(
            currentAnnotations.map((annotation) => annotation.id),
            'point-annotation'
          ),
          anchorTimestamp: anchor.anchorTimestamp,
          anchorYRatio: anchor.anchorYRatio,
          sensorId: anchor.sensorId,
        },
      ])
    },
    [getClampedAnchor, isActive, setAnnotations]
  )

  const handleOverlayMouseLeave = useCallback(() => {}, [])

  const removeAnnotation = useCallback(
    (annotationId: string) => {
      setAnnotations((currentAnnotations) =>
        currentAnnotations.filter(
          (annotation) => annotation.id !== annotationId
        )
      )
    },
    [setAnnotations]
  )

  const setAnnotationSensorId = useCallback(
    (annotationId: string, sensorId: string) => {
      setAnnotations((currentAnnotations) =>
        currentAnnotations.map((annotation) =>
          annotation.id === annotationId
            ? {
                ...annotation,
                sensorId,
              }
            : annotation
        )
      )
    },
    [setAnnotations]
  )

  const updateAnnotationAnchor = useCallback(
    (
      annotationId: string,
      nextAnchor: {
        anchorTimestamp: number
        anchorYRatio: number
      }
    ) => {
      setAnnotations((currentAnnotations) =>
        currentAnnotations.map((annotation) =>
          annotation.id === annotationId
            ? {
                ...annotation,
                anchorTimestamp: nextAnchor.anchorTimestamp,
                anchorYRatio: nextAnchor.anchorYRatio,
              }
            : annotation
        )
      )
    },
    [setAnnotations]
  )

  return {
    annotations,
    removeAnnotation,
    setAnnotationSensorId,
    updateAnnotationAnchor,
    handleOverlayMouseMove,
    handleOverlayMouseDown,
    handleOverlayMouseLeave,
    visibleSensors: getVisibleSensors(),
  }
}

function getNearestSensorIdAtPosition(
  u: uPlot,
  anchorTimestamp: number,
  mouseXInPlot: number,
  mouseYInPlot: number
): string | null {
  const xValues = u.data[0] as ArrayLike<number> | undefined
  if (!xValues || xValues.length === 0) return null

  let nearestSensorId: string | null = null
  let nearestDistance = Number.POSITIVE_INFINITY

  for (const series of getSeriesInfoFromUPlot(u)) {
    if (!series.show || series.isRegression) continue

    const yValues = u.data[series.dataIndex] as
      | ArrayLike<number | null | undefined>
      | undefined
    if (!yValues) continue

    const nearestValidIndex = findNearestValidIndex(
      xValues,
      yValues,
      anchorTimestamp
    )
    if (nearestValidIndex === -1) continue

    const timestamp = Number(xValues[nearestValidIndex])
    const value = yValues[nearestValidIndex]
    if (
      !Number.isFinite(timestamp) ||
      typeof value !== 'number' ||
      Number.isNaN(value)
    ) {
      continue
    }

    let xPixelInPlot: number
    let yPixelInPlot: number

    try {
      xPixelInPlot = u.valToPos(timestamp, 'x')
      yPixelInPlot = u.valToPos(value, series.scale)
    } catch {
      continue
    }
    const distance = Math.hypot(
      xPixelInPlot - mouseXInPlot,
      yPixelInPlot - mouseYInPlot
    )

    if (distance < nearestDistance) {
      nearestDistance = distance
      nearestSensorId = series.sensorId
    }
  }

  return nearestSensorId
}
